import React, { useEffect, useRef, useState } from 'react';
import { Stage, StageEvents, SubscribeType } from 'amazon-ivs-web-broadcast';
import StagePlayer from '../UnityGame/StagePlayer';
import { MapCamera, MapRoom, projectMapX, readPosition, roomName } from './mapDesign';
import { useFocus } from '../../contexts/FocusContext';
import { FaVideo } from 'react-icons/fa';

/** The same room stage Unity subscribes to, displayed without the Unity decoder. */
export default function LevelOneScreen({ token, playbackUrl, localUserId, room }: { token: string; playbackUrl: string; localUserId: string; room?: MapRoom }) {
  const [camera, setCamera] = useState<MapCamera>();
  const [expanded, setExpanded] = useState(false);
  const { registerOverlay, unregisterOverlay } = useFocus();
  useEffect(() => {
    if (!expanded) return;
    registerOverlay('level-one-room-screen');
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', escape);
    return () => { unregisterOverlay('level-one-room-screen'); window.removeEventListener('keydown', escape); };
  }, [expanded, registerOverlay, unregisterOverlay]);
  useEffect(() => {
    const update = (event: Event) => setCamera((event as CustomEvent<MapCamera>).detail);
    window.addEventListener('LevelOneMapCamera', update);
    return () => window.removeEventListener('LevelOneMapCamera', update);
  }, []);
  const [streams, setStreams] = useState<Map<string, MediaStream>>(new Map());
  const streamsRef = useRef(streams); streamsRef.current = streams;
  const [error, setError] = useState('');
  const [selected, setSelected] = useState('');
  const [attempt, setAttempt] = useState(0);
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (!token || !expanded) return;
    let closed = false;
    setStreams(new Map()); setError('');
    const stage = new Stage(token, {
      stageStreamsToPublish: () => [],
      shouldPublishParticipant: () => false,
      shouldSubscribeToParticipant: (participant: any) => participant.userId === localUserId ? SubscribeType.NONE : SubscribeType.AUDIO_VIDEO,
    });
    stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant: any, incoming: any[]) => {
      if (closed || participant.userId === localUserId) return;
      setStreams(previous => {
        const next = new Map(previous);
        const tracks = [...(next.get(participant.id)?.getTracks() || [])];
        for (const stream of incoming) if (stream.mediaStreamTrack && !tracks.some(t => t.id === stream.mediaStreamTrack.id)) tracks.push(stream.mediaStreamTrack);
        next.set(participant.id, new MediaStream(tracks));
        return next;
      });
    });
    stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (participant: any, removed: any[]) => {
      setStreams(previous => {
        const next = new Map(previous);
        const ids = new Set(removed.map(s => s.mediaStreamTrack?.id));
        const tracks = next.get(participant.id)?.getTracks().filter(t => !ids.has(t.id)) || [];
        if (tracks.length) next.set(participant.id, new MediaStream(tracks)); else next.delete(participant.id);
        return next;
      });
    });
    stage.on(StageEvents.STAGE_PARTICIPANT_LEFT, (participant: any) => {
      setStreams(previous => { const next = new Map(previous); next.delete(participant.id); return next; });
    });
    void stage.join().then(() => { if (closed) stage.leave(); }).catch(() => { if (!closed) setError('The room screen could not connect.'); });
    return () => {
      closed = true;
      for (const stream of Array.from(streamsRef.current.values())) for (const track of stream.getTracks()) track.stop();
      stage.leave();
    };
  }, [token, localUserId, attempt, expanded]);
  const candidates = Array.from(streams.entries()).filter(([, stream]) => stream.getVideoTracks().length > 0);
  const active = candidates.find(([id]) => id === selected) || candidates[0];
  const activeStream = active?.[1];
  const anchor = readPosition(room?.podium_position);
  const x = anchor && camera ? projectMapX(anchor.x, camera) : undefined;
  const y = anchor && camera ? camera.height / 2 + (anchor.z - camera.z) * camera.scale : undefined;
  const onMap = Boolean(x !== undefined && y !== undefined && camera && anchor && Math.abs(anchor.y - camera.y) < 4 && x > 90 && x < camera.width - 100 && y > 90 && y < camera.height - 80);
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    element.srcObject = activeStream || null;
    if (activeStream) void element.play().catch(() => {});
    return () => { element.srcObject = null; };
  }, [activeStream, expanded, onMap]);
  // Represent the authored in-world display with one inexpensive map marker;
  // the actual decoder stays in the centered panel and starts only when needed.
  if (!expanded && onMap) return <button className="level-one-screen-marker" style={{ top: y, left: x }} aria-label={`Open ${room ? roomName(room) : 'room'} screen`} onClick={() => setExpanded(true)}><FaVideo /></button>;
  if (!expanded) return <button className="level-one-screen-shortcut" onClick={() => setExpanded(true)}><FaVideo /> {room ? roomName(room) : 'Room'} screen</button>;
  return <section className={`level-one-room-screen${expanded ? ' is-expanded' : ''}`} aria-label="Room screen" style={{ top: expanded ? '50%' : y, left: expanded ? '50%' : x }}>
    <div className="flex items-center justify-between px-3 py-2 text-sm"><span>{room ? roomName(room) : 'Room'} screen</span><button aria-label={expanded ? 'Minimize screen' : 'Expand screen'} onClick={() => setExpanded(v => !v)}>{expanded ? '−' : '↗'}</button>{candidates.length > 1 && <select aria-label="Choose screen" value={active?.[0]} onChange={e => setSelected(e.target.value)} className="bg-slate-800">{candidates.map(([id], i) => <option key={id} value={id}>Screen {i + 1}</option>)}</select>}</div>
    {activeStream ? <video ref={video} autoPlay playsInline controls muted style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'contain' }} /> : playbackUrl ? <StagePlayer playbackUrl={playbackUrl} /> : <div className="p-3 text-sm"><p>{error || 'No one is sharing in this room yet.'}</p>{error && <button className="mt-2 rounded bg-blue-600 px-3 py-1" onClick={() => setAttempt(value => value + 1)}>Reconnect</button>}</div>}
  </section>;
}
